import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/jobsiq/Navbar";
import Footer from "../components/jobsiq/Footer";
import { ShieldCheck, ArrowUp } from "@phosphor-icons/react";

const SECTIONS = [
    {
        id: "introduction",
        n: "01",
        title: "Introduction",
        body: (
            <p>
                Jobs IQ ("we," "our," "us") is committed to protecting the privacy of our users —
                including job seekers, employers, and partners. This Privacy Policy explains how we
                collect, use, store, and safeguard your personal information when you use our platform.
            </p>
        ),
    },
    {
        id: "info-we-collect",
        n: "02",
        title: "Information we collect",
        body: (
            <ul className="space-y-2 list-disc pl-5">
                <li><strong>Personal information:</strong> name, email address, phone number, professional details, résumés, and employment history.</li>
                <li><strong>Account information:</strong> login credentials, preferences, and settings.</li>
                <li><strong>Usage data:</strong> interactions with the platform, search queries, and analytics data.</li>
                <li><strong>Technical data:</strong> IP address, browser type, device information, and cookies.</li>
                <li><strong>Employer data:</strong> job postings, company details, and recruitment preferences.</li>
            </ul>
        ),
    },
    {
        id: "how-we-use",
        n: "03",
        title: "How we use your information",
        body: (
            <>
                <p>We use collected data to:</p>
                <ul className="mt-2 space-y-2 list-disc pl-5">
                    <li>Match candidates with relevant job opportunities using AI-powered algorithms.</li>
                    <li>Improve platform performance and user experience.</li>
                    <li>Communicate updates, alerts, and service-related information.</li>
                    <li>Ensure compliance with legal and regulatory requirements.</li>
                    <li>Prevent fraud, misuse, and unauthorised access.</li>
                </ul>
            </>
        ),
    },
    {
        id: "sharing",
        n: "04",
        title: "Data sharing & disclosure",
        body: (
            <>
                <p>We do not sell your personal data. We may share information:</p>
                <ul className="mt-2 space-y-2 list-disc pl-5">
                    <li>With employers and recruiters for candidate-job matching.</li>
                    <li>With trusted third-party service providers (e.g., cloud hosting, analytics).</li>
                    <li>When required by law, regulation, or government request.</li>
                    <li>During mergers, acquisitions, or business restructuring, with appropriate safeguards.</li>
                </ul>
            </>
        ),
    },
    {
        id: "security",
        n: "05",
        title: "Data security",
        body: (
            <>
                <p>We implement strict technical and organisational measures to protect your data, including:</p>
                <ul className="mt-2 space-y-2 list-disc pl-5">
                    <li>Encryption of sensitive information.</li>
                    <li>Secure cloud hosting (e.g., Microsoft Azure).</li>
                    <li>Access controls and regular audits.</li>
                    <li>Compliance with industry standards (ISO, GDPR, IT Act).</li>
                </ul>
            </>
        ),
    },
    {
        id: "your-rights",
        n: "06",
        title: "Your rights",
        body: (
            <>
                <p>Depending on applicable laws, you may have rights to:</p>
                <ul className="mt-2 space-y-2 list-disc pl-5">
                    <li>Access, correct, or delete your personal data.</li>
                    <li>Withdraw consent for data processing.</li>
                    <li>Request data portability.</li>
                    <li>Lodge complaints with relevant data protection authorities.</li>
                </ul>
            </>
        ),
    },
    {
        id: "cookies",
        n: "07",
        title: "Cookies & tracking",
        body: (
            <>
                <p>Jobs IQ uses cookies and similar technologies to:</p>
                <ul className="mt-2 space-y-2 list-disc pl-5">
                    <li>Enhance user experience.</li>
                    <li>Track platform usage and performance.</li>
                    <li>Deliver personalised recommendations.</li>
                </ul>
            </>
        ),
    },
    {
        id: "retention",
        n: "08",
        title: "Data retention",
        body: (
            <>
                <p>We retain personal data only as long as necessary to:</p>
                <ul className="mt-2 space-y-2 list-disc pl-5">
                    <li>Provide services.</li>
                    <li>Comply with legal obligations.</li>
                    <li>Resolve disputes and enforce agreements.</li>
                </ul>
            </>
        ),
    },
    {
        id: "international-transfers",
        n: "09",
        title: "International data transfers",
        body: (
            <p>
                If data is transferred outside India, we ensure adequate safeguards are in place,
                consistent with GDPR and other global standards.
            </p>
        ),
    },
    {
        id: "updates",
        n: "10",
        title: "Updates to this policy",
        body: (
            <p>
                We may update this Privacy Policy periodically. Changes will be posted on the
                platform with the "Last Updated" date.
            </p>
        ),
    },
];

const LAST_UPDATED = "February 2026";

export default function Privacy() {
    return (
        <div data-testid="privacy-page">
            <Navbar />

            {/* Hero */}
            <section className="bg-slate-950 text-white relative overflow-hidden">
                <div className="absolute inset-0 grid-lines opacity-30" />
                <div className="relative max-w-[1100px] mx-auto px-6 py-16">
                    <div className="font-mono-plex text-xs uppercase tracking-[0.3em] text-blue-400 inline-flex items-center gap-1.5">
                        <ShieldCheck size={14} weight="fill" /> Legal · Privacy Policy
                    </div>
                    <h1 className="font-display text-5xl sm:text-6xl mt-3 leading-[0.95]">
                        How we handle
                        <br />
                        your data.
                    </h1>
                    <p className="mt-6 text-slate-300 max-w-2xl">
                        Jobs IQ is built on trust. This page is the plain-English contract
                        between you and us: what we collect, why, where it lives, and your rights.
                    </p>
                    <div className="mt-6 font-mono-plex text-xs uppercase tracking-widest text-slate-500">
                        Last updated · {LAST_UPDATED}
                    </div>
                </div>
            </section>

            {/* Content */}
            <section className="max-w-[1100px] mx-auto px-6 py-16 grid md:grid-cols-[220px_1fr] gap-12">
                {/* Sticky TOC */}
                <aside className="hidden md:block">
                    <div className="sticky top-24">
                        <div className="font-mono-plex text-xs uppercase tracking-widest text-slate-500 mb-3">
                            Contents
                        </div>
                        <ol className="space-y-1.5 text-sm">
                            {SECTIONS.map((s) => (
                                <li key={s.id}>
                                    <a
                                        href={`#${s.id}`}
                                        data-testid={`privacy-toc-${s.id}`}
                                        className="text-slate-600 hover:text-blue-600 inline-flex items-baseline gap-2"
                                    >
                                        <span className="font-mono-plex text-[10px] text-slate-400">{s.n}</span>
                                        <span>{s.title}</span>
                                    </a>
                                </li>
                            ))}
                        </ol>
                    </div>
                </aside>

                {/* Sections */}
                <article className="space-y-12">
                    {SECTIONS.map((s) => (
                        <section
                            key={s.id}
                            id={s.id}
                            data-testid={`privacy-section-${s.id}`}
                            className="scroll-mt-24"
                        >
                            <div className="font-mono-plex text-xs uppercase tracking-widest text-blue-600">
                                {s.n}
                            </div>
                            <h2 className="font-display text-3xl mt-1 leading-tight">{s.title}</h2>
                            <div className="mt-4 text-slate-700 leading-relaxed">{s.body}</div>
                        </section>
                    ))}

                    <div className="border-t border-slate-200 pt-8 flex items-center justify-between gap-4 flex-wrap">
                        <Link
                            to="/"
                            data-testid="privacy-back-home"
                            className="text-sm text-blue-600 hover:underline"
                        >
                            ← Back to Jobs IQ
                        </Link>
                        <a
                            href="#top"
                            data-testid="privacy-back-top"
                            onClick={(e) => {
                                e.preventDefault();
                                window.scrollTo({ top: 0, behavior: "smooth" });
                            }}
                            className="text-sm inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 hover:bg-slate-950 hover:text-white"
                        >
                            <ArrowUp size={14} /> Back to top
                        </a>
                    </div>
                </article>
            </section>

            <Footer />
        </div>
    );
}
